import {DeviceSlaveSetting} from "../data/models/DeviceSlaveSetting";
import {AvailableRegister} from "../data/models/AvailableRegister";
import {EditableSetting, EditableSettingByGroupName, UpdatingSetting} from "../data/models/UpdatingSetting";
import {STATUS_TEXTS} from "../components/StatusChip";
import {CurrentSettingsUpdateResult} from "../data/models/CurrentSettingsUpdateResult";


export const getSettingFromRegister = (register: number,
                                       settings: DeviceSlaveSetting[]): DeviceSlaveSetting | undefined => {
    return settings?.find((setting) => setting.reg === register);
}

export const getRegisterByNumber = (register: number,
                                    registers: AvailableRegister[]): AvailableRegister | undefined => {
    return registers?.find((reg) => reg.reg === register);
}


export const getFormattedDate = ({dateToFormat, withSeconds, locale}: {
    dateToFormat: string,
    locale?: string,
    withSeconds?: boolean
}): string => {
    const date = new Date(dateToFormat);
    //@ts-ignore
    const userLocale = locale ?? Cookies.get("org.springframework.web.servlet.i18n.CookieLocaleResolver.LOCALE")
    if (withSeconds) {
        return date.toLocaleDateString(userLocale);
    }
    return date.toLocaleString(userLocale, {dateStyle: "medium", timeStyle: "short"});
}

export const getEditingSettings = (settings: DeviceSlaveSetting[],
                                   availableRegisters: AvailableRegister[]): EditableSetting[] => {
    return availableRegisters.map((register) => {
        const currentSetting = getSettingFromRegister(register?.reg, settings);
        return {
            notes: register?.notes,
            label: register?.label,
            reg: register?.reg,
            name: register?.name,
            default: register?.default,
            min: register?.min,
            max: register?.max,
            current: currentSetting?.value,
            newValue: currentSetting?.newValue,
            rw: register?.rw,
            value: currentSetting?.value,
            updatedAt: currentSetting?.updatedAt,
            groupName: register?.groupName,
            um: register?.um,
        }
    })
}

export const groupEditableSettingsByGroupName = (registers: EditableSetting[]): EditableSettingByGroupName[] => {
    const groups = registers.reduce((acc, register) => {
        if (!acc[register.groupName]) {
            acc[register.groupName] = [];
        }
        acc[register.groupName].push(register);
        return acc;
    }, {} as { [key: string]: AvailableRegister[] });

    return Object.keys(groups).map((groupName) => {
        return {
            groupName,
            registers: groups[groupName]
        }
    })
}
export const isUndefinedOrNullValue = (value: any) => {
    return value === undefined || value === null
}

export const isNumber = (value: any) => {
    return !isNaN(Number(value))
}

export const isErrorStatus = (updatingSetting: UpdatingSetting): boolean => {
    return updatingSetting?.anomaly;
}

const isWritingStatus = (updatingSetting: UpdatingSetting): boolean => {
    const undefinedValue = updatingSetting?.expectedValue === null || updatingSetting?.expectedValue === undefined;
    return !undefinedValue;
}

const isReadingStatus = (updatingSetting: UpdatingSetting): boolean => {
    return updatingSetting && isUndefinedOrNullValue(updatingSetting?.expectedValue) && isUndefinedOrNullValue(
        updatingSetting?.validated);
}

const isValidatingStatus = (updatingSetting: UpdatingSetting, isValidating: boolean): boolean => {
    const undefinedValue = updatingSetting?.receivedValue === null || updatingSetting?.receivedValue === undefined;
    return undefinedValue && isValidating && isWritingStatus(updatingSetting) && !updatingSetting?.validated;
}

const isDoneStatus = (updatingSetting: UpdatingSetting): boolean => {
    const undefinedValue = isUndefinedOrNullValue(updatingSetting?.receivedValue);
    const isANumber = isNumber(updatingSetting?.receivedValue);

    const isReading = isReadingStatus(updatingSetting);
    const isWriting = isWritingStatus(updatingSetting);

    return (!undefinedValue && isANumber) && (isReading || (isWriting && updatingSetting?.validated));
}

export const getStatus = (updatingSetting: UpdatingSetting,
                          isValidating: boolean): STATUS_TEXTS => {

    switch (true) {
        case isErrorStatus(updatingSetting):
            return STATUS_TEXTS.ERROR;
        case isDoneStatus(updatingSetting):
            return STATUS_TEXTS.COMPLETED;
        case isValidatingStatus(updatingSetting, isValidating):
            return STATUS_TEXTS.VALIDATING;
        case isWritingStatus(updatingSetting):
            return STATUS_TEXTS.WRITING;
        case isReadingStatus(updatingSetting):
            return STATUS_TEXTS.READING;
        default:
            return STATUS_TEXTS.NONE;
    }
}


export const parseValue = (value: string): number => {
    return Number(value.replace(',', '.'))
}


export const capitalizeFirstLetter = (string: string): string => {
    return (string ?? "").charAt(0).toUpperCase() + (string ?? "").slice(1).toLowerCase();
}


export const getUnitOfMeasure = (unitOfMeasure: string): string => {
    return unitOfMeasure ? " " + unitOfMeasure : "";
}


/** Filter data based on the text filed in the search bar */
export const filterHistoryData = (filterText: string,
                                  getStatusText: (status: STATUS_TEXTS) => string) => (data: CurrentSettingsUpdateResult): boolean => {
    return [
        data?.errorMessage,
        getStatusText(capitalizeFirstLetter(data?.status) as STATUS_TEXTS),
        data?.user,
        getFormattedDate({dateToFormat: data?.startedAt}),
        getFormattedDate({dateToFormat: data?.concludedAt}),
        ...(data?.changes ?? []).map(change => change?.name),
        ...(data?.anomalies ?? []).map(anomaly => anomaly?.errorMessage)
    ].some(field => field?.toLowerCase().includes(filterText?.toLowerCase()));
}


/** Filter settings only if at least a field of EditableSetting contain the filter text*/
export const filterTableData = (filterText: string) => (settings: EditableSetting): boolean => {
    return [
        settings?.name,
        settings?.label,
        settings?.updatedAt
    ].some(field => field?.toLowerCase().includes(filterText?.toLowerCase()));
}